import { SaveResult } from './navigation';

const API_BASES = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
  ? ['http://localhost:8000']
  : [''];

async function tryFetch(path: string, init?: RequestInit) {
  let lastErr: any = null;
  for (const base of API_BASES) {
    try {
      const url = base ? `${base}${path}` : path;
      const res = await fetch(url, init);
      if (res.ok) return res;
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Network error');
}

async function parseJson(res: Response) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('application/json')) return await res.json();
  const text = await res.text().catch(() => '');
  const snippet = text ? text.slice(0, 200) : '';
  throw new Error(`Expected JSON but got ${ct || 'unknown content-type'}${snippet ? `: ${snippet}` : ''}`);
}

export type IntegrationData = Record<string, any>;
export type IntegrationListItem = { id: string; createdAt: string; data: IntegrationData };

export async function loadIntegration(provider: string): Promise<IntegrationData | null> {
  // Try each base and only accept a valid JSON object. This avoids the SPA dev
  // server returning index.html with 200 OK on unknown paths.
  for (const base of API_BASES) {
    try {
      const url = base ? `${base}/save_integration.php?provider=${encodeURIComponent(provider)}` : `/save_integration.php?provider=${encodeURIComponent(provider)}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) continue;
      const ct = res.headers.get('content-type') || '';
      if (ct && !ct.toLowerCase().includes('application/json')) {
        // Not JSON, likely HTML from SPA fallback
        continue;
      }
      const data = await res.json().catch(() => null);
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        return data as IntegrationData;
      }
    } catch {
      continue;
    }
  }
  return null;
}

export async function saveIntegration(provider: string, data: IntegrationData): Promise<SaveResult> {
  for (const base of API_BASES) {
    try {
      const url = base ? `${base}/save_integration.php` : '/save_integration.php';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, data }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j && j.error) msg = j.error;
        } catch {}
        continue;
      }
      return { ok: true };
    } catch {
      continue;
    }
  }
  return { ok: false, message: 'Could not reach PHP endpoint. Is the PHP server running?' };
}

export async function loadIntegrationList(provider: string): Promise<IntegrationListItem[]> {
  for (const base of API_BASES) {
    try {
      const url = base ? `${base}/list_integrations.php?provider=${encodeURIComponent(provider)}` : `/list_integrations.php?provider=${encodeURIComponent(provider)}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) continue;
      const data = await parseJson(res);
      if (Array.isArray(data)) return data as IntegrationListItem[];
    } catch {
      continue;
    }
  }
  return [];
}

export async function deleteIntegration(provider: string, id: string): Promise<SaveResult> {
  for (const base of API_BASES) {
    try {
      const url = base ? `${base}/delete_integration.php` : '/delete_integration.php';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, id }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j && j.error) msg = j.error;
        } catch {}
        continue;
      }
      return { ok: true };
    } catch {
      continue;
    }
  }
  return { ok: false, message: 'Could not reach PHP endpoint. Is the PHP server running?' };
}
